const FALLBACK_HEX = '#808080'
const WHITE = '#ffffff'
const BLACK = '#000000'
const SIX_DIGIT_HEX_PATTERN = /^#[0-9a-f]{6}$/i

export function isHexColor(value: string | null | undefined): value is string {
  return !!value && SIX_DIGIT_HEX_PATTERN.test(value)
}

type OkLab = [lightness: number, a: number, b: number]

function parseHex(hex: string | undefined): string | undefined {
  const shortMatch = hex?.match(/^#([0-9a-f])([0-9a-f])([0-9a-f])$/i)
  if (shortMatch) {
    const [, r, g, b] = shortMatch
    return `#${r}${r}${g}${g}${b}${b}`.toLowerCase()
  }

  return isHexColor(hex) ? hex.toLowerCase() : undefined
}

function srgbToLinear(channel: number): number {
  return channel <= 0.04045 ? channel / 12.92 : ((channel + 0.055) / 1.055) ** 2.4
}

function hexToLinearRgb(hex: string): [red: number, green: number, blue: number] {
  const [red, green, blue] = [1, 3, 5].map((start) =>
    srgbToLinear(Number.parseInt(hex.slice(start, start + 2), 16) / 255)
  )
  return [red, green, blue]
}

function getRelativeLuminance(hex: string): number {
  const [red, green, blue] = hexToLinearRgb(hex)
  return 0.2126 * red + 0.7152 * green + 0.0722 * blue
}

export function getReadableTextColor(backgroundColor: string | undefined): string | undefined {
  if (!isHexColor(backgroundColor)) {
    return undefined
  }
  const luminance = getRelativeLuminance(backgroundColor)
  const contrastWithWhite = 1.05 / (luminance + 0.05)
  const contrastWithBlack = (luminance + 0.05) / 0.05
  return contrastWithWhite >= contrastWithBlack ? WHITE : BLACK
}

function hexToOkLab(hex: string): OkLab {
  const [red, green, blue] = hexToLinearRgb(hex)
  const long = Math.cbrt(0.4122214708 * red + 0.5363325363 * green + 0.0514459929 * blue)
  const medium = Math.cbrt(0.2119034982 * red + 0.6806995451 * green + 0.1073969566 * blue)
  const short = Math.cbrt(0.0883024619 * red + 0.2817188376 * green + 0.6299787005 * blue)

  return [
    0.2104542553 * long + 0.793617785 * medium - 0.0040720468 * short,
    1.9779984951 * long - 2.428592205 * medium + 0.4505937099 * short,
    0.0259040371 * long + 0.7827717662 * medium - 0.808675766 * short,
  ]
}

function okLabDistance(first: OkLab, second: OkLab): number {
  return Math.hypot(first[0] - second[0], first[1] - second[1], first[2] - second[2])
}

function pickInContrastOrder(base: string, candidates: string[]): string[] {
  const taken = [hexToOkLab(base)]
  const remaining = candidates.map((hex) => ({ hex, lab: hexToOkLab(hex) }))
  const picked: string[] = []

  while (remaining.length > 0) {
    const distances = remaining.map(({ lab }) => Math.min(...taken.map((takenLab) => okLabDistance(lab, takenLab))))
    const farthest = distances.reduce((best, distance, index) => (distance > distances[best] ? index : best), 0)
    const [{ hex, lab }] = remaining.splice(farthest, 1)
    picked.push(hex)
    taken.push(lab)
  }

  return picked
}

export function getContrastingColors(baseHex: string | undefined, count: number, palette: readonly string[]): string[] {
  const base = parseHex(baseHex) ?? FALLBACK_HEX
  const candidates = palette.map(parseHex).filter((hex): hex is string => hex !== undefined && hex !== base)
  const contrastOrder = pickInContrastOrder(base, candidates)

  return Array.from({ length: Math.max(count, 1) }, (_, slot) =>
    slot === 0 || contrastOrder.length === 0 ? base : contrastOrder[(slot - 1) % contrastOrder.length]
  )
}
